import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Save,
  Download,
  Upload,
  Eye,
  AlertCircle,
  Droplet,
  Settings,
} from "lucide-react";
import {
  AccessibleColorSwatch,
  AccessibleInkLevelIndicator,
} from "./AccessibleColorSwatch";
import type { SubstrateProfile } from "../types/substrate";

interface SubstrateEditorProps {
  initialSubstrate?: Partial<SubstrateProfile>;
  onSave?: (substrate: SubstrateProfile) => void;
  onCancel?: () => void;
  className?: string;
}

interface ValidationErrors {
  [key: string]: string[];
}

interface SubstrateTemplate {
  name: string;
  description: string;
  substrate: Partial<SubstrateProfile>;
}

const SUBSTRATE_TEMPLATES: SubstrateTemplate[] = [
  {
    name: "Standard Coated Paper",
    description: "Typical glossy coated paper for commercial printing",
    substrate: {
      category: "paper",
      brightness: 85,
      glossiness: 75,
      tacLimit: 320,
      inkLimits: { cyan: 85, magenta: 85, yellow: 90, black: 85 },
      absorptionCoefficients: {
        cyan: 0.75,
        magenta: 0.7,
        yellow: 0.65,
        black: 0.8,
      },
    },
  },
  {
    name: "High Gloss Metallic",
    description: "Premium metallic substrate with high reflectivity",
    substrate: {
      category: "metallic",
      brightness: 70,
      glossiness: 95,
      tacLimit: 280,
      inkLimits: { cyan: 70, magenta: 70, yellow: 75, black: 70 },
      absorptionCoefficients: {
        cyan: 0.85,
        magenta: 0.8,
        yellow: 0.75,
        black: 0.9,
      },
    },
  },
  {
    name: "Matte Plastic",
    description: "Low-gloss plastic substrate for packaging",
    substrate: {
      category: "plastic",
      brightness: 75,
      glossiness: 20,
      tacLimit: 300,
      inkLimits: { cyan: 80, magenta: 80, yellow: 85, black: 80 },
      absorptionCoefficients: {
        cyan: 0.65,
        magenta: 0.6,
        yellow: 0.55,
        black: 0.7,
      },
    },
  },
  {
    name: "Canvas Fabric",
    description: "Textured fabric substrate for artistic prints",
    substrate: {
      category: "fabric",
      brightness: 65,
      glossiness: 10,
      tacLimit: 340,
      inkLimits: { cyan: 90, magenta: 90, yellow: 95, black: 90 },
      absorptionCoefficients: {
        cyan: 0.55,
        magenta: 0.5,
        yellow: 0.45,
        black: 0.6,
      },
    },
  },
];

export function SubstrateEditor({
  initialSubstrate,
  onSave,
  onCancel,
  className = "",
}: SubstrateEditorProps) {
  const [activeTab, setActiveTab] = useState<"basic" | "ink" | "advanced">(
    "basic",
  );
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  // Refs for focus management
  const firstInputRef = useRef<HTMLInputElement>(null);
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const formRef = useRef<HTMLFormElement>(null);

  const [substrate, setSubstrate] = useState<Partial<SubstrateProfile>>({
    id: "",
    version: "1.0.0",
    name: "",
    category: "paper",
    manufacturer: "",
    brightness: 80,
    glossiness: 50,
    absorptionCoefficients: {
      cyan: 0.7,
      magenta: 0.7,
      yellow: 0.7,
      black: 0.7,
    },
    baseColor: {
      hex: "#ffffff",
      rgb: { r: 255, g: 255, b: 255 },
    },
    tacLimit: 300,
    inkLimits: {
      cyan: 85,
      magenta: 85,
      yellow: 90,
      black: 85,
    },
    metadata: {
      description: "",
      applications: [],
      recommendedUse: "",
      environmentalInfo: "",
    },
    ...initialSubstrate,
  });

  // Validate substrate in real-time
  const validateSubstrate = useCallback(
    (data: Partial<SubstrateProfile>): ValidationErrors => {
      const newErrors: ValidationErrors = {};

      // Basic validation
      if (!data.name?.trim()) {
        newErrors.name = ["Name is required"];
      }
      if (!data.manufacturer?.trim()) {
        newErrors.manufacturer = ["Manufacturer is required"];
      }
      if (!data.category) {
        newErrors.category = ["Category is required"];
      }

      // Numeric validation
      if (data.brightness !== undefined) {
        if (data.brightness < 0 || data.brightness > 100) {
          newErrors.brightness = ["Brightness must be between 0 and 100"];
        }
      }
      if (data.glossiness !== undefined) {
        if (data.glossiness < 0 || data.glossiness > 100) {
          newErrors.glossiness = ["Glossiness must be between 0 and 100"];
        }
      }
      if (data.tacLimit !== undefined) {
        if (data.tacLimit < 100 || data.tacLimit > 400) {
          newErrors.tacLimit = ["TAC limit must be between 100 and 400"];
        }
      }

      // Ink limits validation
      if (data.inkLimits) {
        Object.entries(data.inkLimits).forEach(([channel, limit]) => {
          if (limit < 0 || limit > 100) {
            if (!newErrors.inkLimits) newErrors.inkLimits = [];
            newErrors.inkLimits.push(
              `${channel} limit must be between 0 and 100`,
            );
          }
        });

        // Check if individual limits exceed TAC
        const totalInk = Object.values(data.inkLimits).reduce(
          (sum, limit) => sum + limit,
          0,
        );
        if (data.tacLimit && totalInk > data.tacLimit * 1.5) {
          if (!newErrors.inkLimits) newErrors.inkLimits = [];
          newErrors.inkLimits.push(
            "Sum of individual ink limits should not exceed 1.5x TAC limit",
          );
        }
      }

      // Absorption coefficients validation
      if (data.absorptionCoefficients) {
        Object.entries(data.absorptionCoefficients).forEach(
          ([channel, coeff]) => {
            if (coeff < 0 || coeff > 1) {
              if (!newErrors.absorptionCoefficients)
                newErrors.absorptionCoefficients = [];
              newErrors.absorptionCoefficients.push(
                `${channel} absorption must be between 0 and 1`,
              );
            }
          },
        );
      }

      // Base color validation
      if (data.baseColor?.hex && !/^#[0-9a-f]{6}$/i.test(data.baseColor.hex)) {
        newErrors.baseColor = ["Invalid hex color format"];
      }

      return newErrors;
    },
    [],
  );

  // Track announcement elements for cleanup
  const announcementRefs = useRef<Node[]>([]);

  // Announce messages to screen readers
  const announceToScreenReader = useCallback((message: string) => {
    const announcement = document.createElement("div");
    announcement.setAttribute("aria-live", "polite");
    announcement.setAttribute("aria-atomic", "true");
    announcement.className = "sr-only";
    announcement.textContent = message;
    document.body.appendChild(announcement);

    // Track for cleanup
    announcementRefs.current.push(announcement);

    // Clean up after announcement to prevent memory leaks
    const cleanup = () => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
      // Remove from tracking array
      const index = announcementRefs.current.indexOf(announcement);
      if (index > -1) {
        announcementRefs.current.splice(index, 1);
      }
    };

    setTimeout(cleanup, 1000);
  }, []);

  // Real-time validation with accessibility announcements
  useEffect(() => {
    const newErrors = validateSubstrate(substrate);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setErrors(newErrors);

    // Announce errors to screen readers
    if (Object.keys(newErrors).length > 0) {
      const errorCount = Object.values(newErrors).reduce(
        (sum, errors) => sum + errors.length,
        0,
      );
      announceToScreenReader(
        `${errorCount} validation error${errorCount !== 1 ? "s" : ""} found`,
      );
    }
  }, [substrate, validateSubstrate, announceToScreenReader]);

  // Focus first input on mount
  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up any remaining announcement elements
      announcementRefs.current.forEach((announcement) => {
        if (document.body.contains(announcement)) {
          document.body.removeChild(announcement);
        }
      });
      announcementRefs.current = [];
    };
  }, []);

  // Handle tab navigation with keyboard
  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent, tabId: string) => {
      const tabs = ["basic", "ink", "advanced"] as const;
      const currentIndex = tabs.indexOf(tabId as "basic" | "ink" | "advanced");

      switch (e.key) {
        case "ArrowLeft": {
          e.preventDefault();
          const prevTab = tabs[currentIndex - 1] || tabs[tabs.length - 1];
          tabRefs.current[prevTab]?.focus();
          setActiveTab(prevTab);
          break;
        }
        case "ArrowRight": {
          e.preventDefault();
          const nextTab = tabs[(currentIndex + 1) % tabs.length];
          tabRefs.current[nextTab]?.focus();
          setActiveTab(nextTab);
          break;
        }
        case "Home":
          e.preventDefault();
          tabRefs.current.basic?.focus();
          setActiveTab("basic");
          break;
        case "End":
          e.preventDefault();
          tabRefs.current.advanced?.focus();
          setActiveTab("advanced");
          break;
      }
    },
    [],
  );

  const handleFieldChange = useCallback((field: string, value: unknown) => {
    setSubstrate((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  }, []);

  const handleNestedFieldChange = useCallback(
    (parent: string, field: string, value: unknown) => {
      setSubstrate((prev) => {
        const parentValue = prev[parent as keyof SubstrateProfile];
        return {
          ...prev,
          [parent]:
            parentValue && typeof parentValue === "object"
              ? {
                  ...parentValue,
                  [field]: value,
                }
              : value,
        };
      });
      setIsDirty(true);
    },
    [],
  );

  const handleTemplateSelect = useCallback((template: SubstrateTemplate) => {
    const newSubstrate = {
      ...template.substrate,
      id: `custom-${Date.now()}`,
      name: template.name,
      version: "1.0.0",
      manufacturer: "Custom",
      baseColor: { hex: "#ffffff", rgb: { r: 255, g: 255, b: 255 } },
      metadata: {
        description: template.description,
        applications: [],
        recommendedUse: "",
        environmentalInfo: "",
      },
    };
    setSubstrate(newSubstrate);
    setShowTemplateSelector(false);
    setIsDirty(true);
  }, []);

  const handleSave = useCallback(() => {
    if (!isValid) return;

    const completeSubstrate: SubstrateProfile = {
      id: substrate.id || `custom-${Date.now()}`,
      version: substrate.version || "1.0.0",
      name: substrate.name || "",
      category: substrate.category || "paper",
      manufacturer: substrate.manufacturer || "",
      brightness: substrate.brightness || 80,
      glossiness: substrate.glossiness || 50,
      absorptionCoefficients: substrate.absorptionCoefficients || {
        cyan: 0.7,
        magenta: 0.7,
        yellow: 0.7,
        black: 0.7,
      },
      baseColor: substrate.baseColor || {
        hex: "#ffffff",
        rgb: { r: 255, g: 255, b: 255 },
      },
      tacLimit: substrate.tacLimit || 300,
      inkLimits: substrate.inkLimits || {
        cyan: 85,
        magenta: 85,
        yellow: 90,
        black: 85,
      },
      dotGain: substrate.dotGain || {
        cyan: 15,
        magenta: 15,
        yellow: 15,
        black: 15,
      },
      metadata: substrate.metadata || {
        description: "",
        applications: [],
        recommendedUse: "",
        environmentalInfo: "",
      },
    };

    // Save to localStorage
    try {
      const savedSubstrates = JSON.parse(
        localStorage.getItem("customSubstrates") || "[]",
      );
      savedSubstrates.push(completeSubstrate);
      localStorage.setItem("customSubstrates", JSON.stringify(savedSubstrates));
    } catch (error) {
      console.error("Failed to save to localStorage:", error);
    }

    onSave?.(completeSubstrate);
  }, [substrate, isValid, onSave]);

  const handleExport = useCallback(() => {
    if (!isValid) return;

    const exportData = {
      ...substrate,
      id: substrate.id || `custom-${Date.now()}`,
      exportedAt: new Date().toISOString(),
      version: substrate.version || "1.0.0",
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${substrate.name || "custom-substrate"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [substrate, isValid]);

  const handleImport = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target?.result as string);
          setSubstrate(imported);
          setIsDirty(true);
        } catch (error) {
          console.error("Failed to import substrate:", error);
        }
      };
      reader.readAsText(file);
    },
    [],
  );

  const renderBasicTab = () => (
    <div className="space-y-6" role="tabpanel" aria-labelledby="basic-tab">
      {/* Template Selector */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-blue-900">Quick Start</h4>
            <p className="text-sm text-blue-700">
              Start with a pre-configured template
            </p>
          </div>
          <button
            onClick={() => setShowTemplateSelector(!showTemplateSelector)}
            className="px-3 py-1 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            aria-expanded={showTemplateSelector}
            aria-controls="template-selector"
          >
            {showTemplateSelector ? "Hide" : "Show"} Templates
          </button>
        </div>

        {showTemplateSelector && (
          <div
            id="template-selector"
            className="mt-4 grid grid-cols-2 gap-3"
            role="list"
            aria-label="Substrate templates"
          >
            {SUBSTRATE_TEMPLATES.map((template, index) => (
              <button
                key={index}
                onClick={() => handleTemplateSelect(template)}
                className="p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-left focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                role="listitem"
                aria-describedby={`template-${index}-desc`}
              >
                <h5 className="font-medium text-gray-900">{template.name}</h5>
                <p
                  id={`template-${index}-desc`}
                  className="text-xs text-gray-600 mt-1"
                >
                  {template.description}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>

        {/* Name Field */}
        <div>
          <label
            htmlFor="substrate-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Substrate Name *
          </label>
          <input
            id="substrate-name"
            ref={firstInputRef}
            type="text"
            value={substrate.name || ""}
            onChange={(e) => handleFieldChange("name", e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.name ? "border-red-500" : "border-gray-300"
            }`}
            aria-describedby="substrate-name-error substrate-name-help"
            aria-invalid={!!errors.name}
            required
          />
          <p id="substrate-name-help" className="text-xs text-gray-500 mt-1">
            Enter a descriptive name for this substrate profile
          </p>
          {errors.name && (
            <p
              id="substrate-name-error"
              className="text-sm text-red-600 mt-1"
              role="alert"
            >
              {errors.name[0]}
            </p>
          )}
        </div>

        {/* Category Field */}
        <div>
          <label
            htmlFor="substrate-category"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Category *
          </label>
          <select
            id="substrate-category"
            value={substrate.category || "paper"}
            onChange={(e) => handleFieldChange("category", e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.category ? "border-red-500" : "border-gray-300"
            }`}
            aria-describedby="substrate-category-error substrate-category-help"
            aria-invalid={!!errors.category}
            required
          >
            <option value="paper">Paper</option>
            <option value="metallic">Metallic</option>
            <option value="plastic">Plastic</option>
            <option value="fabric">Fabric</option>
          </select>
          <p
            id="substrate-category-help"
            className="text-xs text-gray-500 mt-1"
          >
            Select the material category for this substrate
          </p>
          {errors.category && (
            <p
              id="substrate-category-error"
              className="text-sm text-red-600 mt-1"
              role="alert"
            >
              {errors.category[0]}
            </p>
          )}
        </div>

        {/* Manufacturer Field */}
        <div>
          <label
            htmlFor="substrate-manufacturer"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Manufacturer *
          </label>
          <input
            id="substrate-manufacturer"
            type="text"
            value={substrate.manufacturer || ""}
            onChange={(e) => handleFieldChange("manufacturer", e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.manufacturer ? "border-red-500" : "border-gray-300"
            }`}
            aria-describedby="substrate-manufacturer-error substrate-manufacturer-help"
            aria-invalid={!!errors.manufacturer}
            required
          />
          <p
            id="substrate-manufacturer-help"
            className="text-xs text-gray-500 mt-1"
          >
            Enter the manufacturer name
          </p>
          {errors.manufacturer && (
            <p
              id="substrate-manufacturer-error"
              className="text-sm text-red-600 mt-1"
              role="alert"
            >
              {errors.manufacturer[0]}
            </p>
          )}
        </div>

        {/* Description Field */}
        <div>
          <label
            htmlFor="substrate-description"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Description
          </label>
          <textarea
            id="substrate-description"
            value={substrate.metadata?.description || ""}
            onChange={(e) =>
              handleNestedFieldChange("metadata", "description", e.target.value)
            }
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            aria-describedby="substrate-description-help"
          />
          <p
            id="substrate-description-help"
            className="text-xs text-gray-500 mt-1"
          >
            Provide a detailed description of this substrate
          </p>
        </div>
      </div>

      {/* Visual Properties */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Visual Properties</h3>

        <div className="grid grid-cols-2 gap-4">
          {/* Brightness Field */}
          <div>
            <label
              htmlFor="substrate-brightness"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Brightness (%)
            </label>
            <input
              id="substrate-brightness"
              type="number"
              min="0"
              max="100"
              value={substrate.brightness || 80}
              onChange={(e) =>
                handleFieldChange("brightness", parseInt(e.target.value))
              }
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.brightness ? "border-red-500" : "border-gray-300"
              }`}
              aria-describedby="substrate-brightness-error substrate-brightness-help"
              aria-invalid={!!errors.brightness}
            />
            <p
              id="substrate-brightness-help"
              className="text-xs text-gray-500 mt-1"
            >
              Brightness level (0-100)
            </p>
            {errors.brightness && (
              <p
                id="substrate-brightness-error"
                className="text-sm text-red-600 mt-1"
                role="alert"
              >
                {errors.brightness[0]}
              </p>
            )}
          </div>

          {/* Glossiness Field */}
          <div>
            <label
              htmlFor="substrate-glossiness"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Glossiness (%)
            </label>
            <input
              id="substrate-glossiness"
              type="number"
              min="0"
              max="100"
              value={substrate.glossiness || 50}
              onChange={(e) =>
                handleFieldChange("glossiness", parseInt(e.target.value))
              }
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.glossiness ? "border-red-500" : "border-gray-300"
              }`}
              aria-describedby="substrate-glossiness-error substrate-glossiness-help"
              aria-invalid={!!errors.glossiness}
            />
            <p
              id="substrate-glossiness-help"
              className="text-xs text-gray-500 mt-1"
            >
              Glossiness level (0-100)
            </p>
            {errors.glossiness && (
              <p
                id="substrate-glossiness-error"
                className="text-sm text-red-600 mt-1"
                role="alert"
              >
                {errors.glossiness[0]}
              </p>
            )}
          </div>
        </div>

        {/* Base Color Field */}
        <div>
          <label
            htmlFor="substrate-base-color"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Base Color
          </label>
          <div className="flex items-center space-x-3">
            <input
              id="substrate-base-color"
              type="color"
              value={substrate.baseColor?.hex || "#ffffff"}
              onChange={(e) =>
                handleNestedFieldChange("baseColor", "hex", e.target.value)
              }
              className={`h-10 w-20 border rounded cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.baseColor ? "border-red-500" : "border-gray-300"
              }`}
              aria-describedby="substrate-base-color-error substrate-base-color-help"
              aria-invalid={!!errors.baseColor}
            />
            <input
              type="text"
              value={substrate.baseColor?.hex || "#ffffff"}
              onChange={(e) =>
                handleNestedFieldChange("baseColor", "hex", e.target.value)
              }
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
              aria-label="Base color hex code"
              placeholder="#FFFFFF"
            />
            <AccessibleColorSwatch
              color={substrate.baseColor?.hex || "#ffffff"}
              size="md"
              showHex={false}
              ariaLabel="Current base color preview"
            />
          </div>
          <p
            id="substrate-base-color-help"
            className="text-xs text-gray-500 mt-1"
          >
            Select the base color of the substrate
          </p>
          {errors.baseColor && (
            <p
              id="substrate-base-color-error"
              className="text-sm text-red-600 mt-1"
              role="alert"
            >
              {errors.baseColor[0]}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const renderInkTab = () => (
    <div className="space-y-6" role="tabpanel" aria-labelledby="ink-tab">
      <h3 className="text-lg font-medium text-gray-900">Ink Settings</h3>

      {/* TAC Limit */}
      <div>
        <label
          htmlFor="substrate-tac-limit"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Total Area Coverage (TAC) Limit (%)
        </label>
        <input
          id="substrate-tac-limit"
          type="number"
          min="100"
          max="400"
          value={substrate.tacLimit || 300}
          onChange={(e) =>
            handleFieldChange("tacLimit", parseInt(e.target.value))
          }
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.tacLimit ? "border-red-500" : "border-gray-300"
          }`}
          aria-describedby="substrate-tac-limit-error substrate-tac-limit-help"
          aria-invalid={!!errors.tacLimit}
        />
        <p id="substrate-tac-limit-help" className="text-xs text-gray-500 mt-1">
          Maximum total ink coverage (100-400%)
        </p>
        {errors.tacLimit && (
          <p
            id="substrate-tac-limit-error"
            className="text-sm text-red-600 mt-1"
            role="alert"
          >
            {errors.tacLimit[0]}
          </p>
        )}
      </div>

      {/* Individual Ink Limits */}
      <div>
        <h4 className="text-md font-medium text-gray-900 mb-3">
          Individual Ink Limits (%)
        </h4>
        <div className="grid grid-cols-2 gap-4">
          {(["cyan", "magenta", "yellow", "black"] as const).map((color) => (
            <div key={color}>
              <label
                htmlFor={`substrate-ink-${color}`}
                className="block text-sm font-medium text-gray-700 mb-1 capitalize"
              >
                {color}
              </label>
              <input
                id={`substrate-ink-${color}`}
                type="number"
                min="0"
                max="100"
                value={substrate.inkLimits?.[color] || 85}
                onChange={(e) =>
                  handleNestedFieldChange(
                    "inkLimits",
                    color,
                    parseInt(e.target.value),
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                aria-describedby={`substrate-ink-${color}-help`}
              />
              <p
                id={`substrate-ink-${color}-help`}
                className="text-xs text-gray-500 mt-1"
              >
                Maximum {color} ink coverage (0-100%)
              </p>
              <AccessibleInkLevelIndicator
                percentage={substrate.inkLimits?.[color] || 85}
                limit={substrate.tacLimit || 300}
                color={
                  color === "cyan"
                    ? "#00FFFF"
                    : color === "magenta"
                      ? "#FF00FF"
                      : color === "yellow"
                        ? "#FFFF00"
                        : "#000000"
                }
                label={`${color} ink limit`}
              />
            </div>
          ))}
        </div>
        {errors.inkLimits && (
          <div className="mt-2 text-sm text-red-600" role="alert">
            {errors.inkLimits.map((error, index) => (
              <p key={index}>{error}</p>
            ))}
          </div>
        )}
      </div>

      {/* Absorption Coefficients */}
      <div>
        <h4 className="text-md font-medium text-gray-900 mb-3">
          Absorption Coefficients
        </h4>
        <div className="grid grid-cols-2 gap-4">
          {(["cyan", "magenta", "yellow", "black"] as const).map((color) => (
            <div key={color}>
              <label
                htmlFor={`substrate-absorption-${color}`}
                className="block text-sm font-medium text-gray-700 mb-1 capitalize"
              >
                {color} Absorption
              </label>
              <input
                id={`substrate-absorption-${color}`}
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={substrate.absorptionCoefficients?.[color] || 0.7}
                onChange={(e) =>
                  handleNestedFieldChange(
                    "absorptionCoefficients",
                    color,
                    parseFloat(e.target.value),
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                aria-describedby={`substrate-absorption-${color}-help`}
              />
              <p
                id={`substrate-absorption-${color}-help`}
                className="text-xs text-gray-500 mt-1"
              >
                {color} ink absorption rate (0.0-1.0)
              </p>
            </div>
          ))}
        </div>
        {errors.absorptionCoefficients && (
          <div className="mt-2 text-sm text-red-600" role="alert">
            {errors.absorptionCoefficients.map((error, index) => (
              <p key={index}>{error}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderAdvancedTab = () => (
    <div className="space-y-6" role="tabpanel" aria-labelledby="advanced-tab">
      <h3 className="text-lg font-medium text-gray-900">Advanced Settings</h3>

      {/* Version */}
      <div>
        <label
          htmlFor="substrate-version"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Version
        </label>
        <input
          id="substrate-version"
          type="text"
          value={substrate.version || "1.0.0"}
          onChange={(e) => handleFieldChange("version", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          aria-describedby="substrate-version-help"
          placeholder="1.0.0"
        />
        <p id="substrate-version-help" className="text-xs text-gray-500 mt-1">
          Profile version (semantic versioning)
        </p>
      </div>

      {/* Applications */}
      <div>
        <label
          htmlFor="substrate-applications"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Applications
        </label>
        <input
          id="substrate-applications"
          type="text"
          value={substrate.metadata?.applications?.join(", ") || ""}
          onChange={(e) =>
            handleNestedFieldChange(
              "metadata",
              "applications",
              e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          aria-describedby="substrate-applications-help"
          placeholder="Commercial printing, Packaging, Labels"
        />
        <p
          id="substrate-applications-help"
          className="text-xs text-gray-500 mt-1"
        >
          Comma-separated list of suitable applications
        </p>
      </div>

      {/* Recommended Use */}
      <div>
        <label
          htmlFor="substrate-recommended-use"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Recommended Use
        </label>
        <textarea
          id="substrate-recommended-use"
          value={substrate.metadata?.recommendedUse || ""}
          onChange={(e) =>
            handleNestedFieldChange(
              "metadata",
              "recommendedUse",
              e.target.value,
            )
          }
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          aria-describedby="substrate-recommended-use-help"
        />
        <p
          id="substrate-recommended-use-help"
          className="text-xs text-gray-500 mt-1"
        >
          Describe recommended use cases and conditions
        </p>
      </div>

      {/* Environmental Info */}
      <div>
        <label
          htmlFor="substrate-environmental-info"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Environmental Information
        </label>
        <textarea
          id="substrate-environmental-info"
          value={substrate.metadata?.environmentalInfo || ""}
          onChange={(e) =>
            handleNestedFieldChange(
              "metadata",
              "environmentalInfo",
              e.target.value,
            )
          }
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          aria-describedby="substrate-environmental-info-help"
        />
        <p
          id="substrate-environmental-info-help"
          className="text-xs text-gray-500 mt-1"
        >
          Environmental impact, recycling information, certifications
        </p>
      </div>

      {/* Color Transform Matrix */}
      <div>
        <h4 className="text-md font-medium text-gray-900 mb-3">
          Color Transform Matrix
        </h4>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-3">
            Advanced color transformation matrix for CMYK color space conversion
          </p>
          <button
            type="button"
            onClick={() => {
              const defaultMatrix = [
                [1, 0, 0, 0, 0],
                [0, 1, 0, 0, 0],
                [0, 0, 1, 0, 0],
                [0, 0, 0, 1, 0],
              ];
              handleFieldChange("colorTransform", {
                matrix: defaultMatrix,
                type: "linear",
                inputSpace: "cmyk",
                outputSpace: "cmyk",
                gamma: 1.0,
              });
            }}
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Reset to Default
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}
    >
      <form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Substrate Editor
            </h2>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={handleExport}
                disabled={!isValid}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label="Export substrate profile"
              >
                <Download className="w-4 h-4" aria-hidden="true" />
              </button>
              <label className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                <Upload className="w-4 h-4" aria-hidden="true" />
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="hidden"
                  aria-label="Import substrate profile"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex" role="tablist" aria-label="Substrate settings">
            {[
              { id: "basic", label: "Basic Info", icon: Settings },
              { id: "ink", label: "Ink Settings", icon: Droplet },
              { id: "advanced", label: "Advanced", icon: Eye },
            ].map((tab) => (
              <button
                key={tab.id}
                ref={(el) => {
                  tabRefs.current[tab.id] = el;
                }}
                type="button"
                onClick={() =>
                  setActiveTab(tab.id as "basic" | "ink" | "advanced")
                }
                onKeyDown={(e) => handleTabKeyDown(e, tab.id)}
                className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                role="tab"
                id={`${tab.id}-tab`}
                aria-selected={activeTab === tab.id}
                aria-controls={`${tab.id}-panel`}
                tabIndex={activeTab === tab.id ? 0 : -1}
              >
                <tab.icon className="w-4 h-4 mr-2" aria-hidden="true" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Validation Summary */}
          {Object.keys(errors).length > 0 && (
            <div
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
              role="alert"
              aria-live="polite"
            >
              <div className="flex items-start">
                <AlertCircle
                  className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0"
                  aria-hidden="true"
                />
                <div>
                  <h3 className="text-sm font-medium text-red-800">
                    Please fix the following errors:
                  </h3>
                  <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                    {Object.entries(errors).map(([field, fieldErrors]) =>
                      fieldErrors.map((error, index) => (
                        <li key={`${field}-${index}`}>{error}</li>
                      )),
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Tab Panels */}
          <div
            id="basic-panel"
            role="tabpanel"
            aria-labelledby="basic-tab"
            hidden={activeTab !== "basic"}
          >
            {renderBasicTab()}
          </div>

          <div
            id="ink-panel"
            role="tabpanel"
            aria-labelledby="ink-tab"
            hidden={activeTab !== "ink"}
          >
            {renderInkTab()}
          </div>

          <div
            id="advanced-panel"
            role="tabpanel"
            aria-labelledby="advanced-tab"
            hidden={activeTab !== "advanced"}
          >
            {renderAdvancedTab()}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {isDirty && (
                <span className="flex items-center">
                  <span
                    className="w-2 h-2 bg-orange-400 rounded-full mr-2"
                    aria-hidden="true"
                  ></span>
                  Unsaved changes
                </span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={!isValid || !isDirty}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-describedby="save-button-help"
              >
                <Save className="w-4 h-4 mr-2 inline" aria-hidden="true" />
                Save Substrate
              </button>
              <span id="save-button-help" className="sr-only">
                {!isValid
                  ? "Please fix validation errors before saving"
                  : !isDirty
                    ? "No changes to save"
                    : "Save substrate profile"}
              </span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
